
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface GameDeletionImpact {
  votes: number;
  feedback: number;
  rsvps: number;
}

/**
 * Returns counts of child rows that will be cascaded when a game is deleted.
 * All counts are exact via head:true requests (no row payload).
 */
export const getGameDeletionImpact = async (gameId: string): Promise<GameDeletionImpact> => {
  const [votesRes, feedbackRes, rsvpsRes] = await Promise.all([
    supabase.from("teamvote_votes").select("*", { count: "exact", head: true }).eq("game_id", gameId),
    supabase.from("coach_feedback").select("*", { count: "exact", head: true }).eq("game_id", gameId),
    supabase.from("game_rsvps").select("*", { count: "exact", head: true }).eq("game_id", gameId),
  ]);
  return {
    votes: votesRes.count ?? 0,
    feedback: feedbackRes.count ?? 0,
    rsvps: rsvpsRes.count ?? 0,
  };
};

/**
 * Deletes a game and all associated votes
 * Votes are automatically deleted due to ON DELETE CASCADE constraint
 */
export const deleteGame = async (gameId: string): Promise<boolean> => {
  try {
    console.log("Starting deletion process for game:", gameId);
    
    // Delete the game - votes will be automatically deleted thanks to the CASCADE constraint
    const { error: gameError } = await supabase
      .from('teamvote_games')
      .delete()
      .eq('id', gameId);
      
    if (gameError) {
      console.error("Error deleting game:", gameError);
      toast.error(`Failed to delete game poll: ${gameError.message}`);
      return false;
    }
    
    console.log("Game successfully deleted");
    toast.success("Game poll deleted successfully");
    return true;
  } catch (error) {
    console.error("Error in delete operation:", error);
    toast.error("Failed to delete game poll");
    return false;
  }
};

/**
 * Subscribe to realtime updates for a game's votes
 * Returns a function to unsubscribe
 */
export const subscribeToGameVotes = (gameId: string, onUpdate: () => void) => {
  // Enable realtime for the teamvote_votes table
  // This is commented out because we're using polling for now
  // But this code can be used in the future if we want to switch to realtime
  
  /*
  const channel = supabase
    .channel('game-votes-changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'teamvote_votes',
        filter: `game_id=eq.${gameId}`
      },
      (payload) => {
        console.log('Vote change received:', payload);
        onUpdate();
      }
    )
    .subscribe();
    
  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
  */
  
  // Placeholder for now since we're using polling
  return () => {};
};
